import { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import * as pdfjs from "pdfjs-dist";
import { WorkerMessageHandler } from "pdfjs-dist/build/pdf.worker.min.mjs";
import { toast } from "react-toastify";

import { CircularLoading } from "./CircularLoading";
import { downloadFile } from "@/utils/fileDownloadHelper";
import { isReactNativeWebView } from "@/utils/useWebViewBridge";


(globalThis as unknown as { pdfjsWorker: { WorkerMessageHandler: unknown } }).pdfjsWorker =
  { WorkerMessageHandler };

pdfjs.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`;

interface MobilePdfViewerProps {
  base64: string;
  fileName?: string;
  onOpenExternally?: () => void;
}

function normalizePdfBase64(raw: string): string {
  const trimmed = (raw || "").trim();
  const comma = trimmed.indexOf(",");
  if (trimmed.startsWith("data:") && comma >= 0) {
    return trimmed.slice(comma + 1);
  }
  return trimmed.replace(/\s/g, "");
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function waitForLayoutWidth(
  el: HTMLElement,
  fallback: number,
): Promise<number> {
  for (let i = 0; i < 8; i += 1) {
    const w = el.clientWidth;
    if (w >= 200) return w;
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));
  }
  return Math.max(el.clientWidth || fallback, 280);
}

/**
 * Pré-visualiza PDF (base64) em canvas no mobile/APK.
 * Download só como fallback se o canvas falhar.
 */
export function MobilePdfViewer({
  base64,
  fileName = "Documento.pdf",
  onOpenExternally,
}: MobilePdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleDownloadFallback = () => {
    try {
      const cleaned = normalizePdfBase64(base64);
      if (!cleaned) {
        toast.error("Arquivo indisponível.");
        return;
      }
      downloadFile(cleaned, fileName, "application/pdf");
      if (isReactNativeWebView()) {
        toast.info("Download iniciado. Verifique a pasta Downloads.");
      }
    } catch (err) {
      console.error("[MobilePdfViewer] download error", err);
      toast.error("Não foi possível baixar o arquivo.");
      onOpenExternally?.();
    }
  };

  useEffect(() => {
    let cancelled = false;
    let pdfDoc: pdfjs.PDFDocumentProxy | null = null;

    async function renderPdf() {
      setLoading(true);
      setError(false);

      const container = containerRef.current;
      const cleaned = normalizePdfBase64(base64);
      if (!container || !cleaned) {
        setLoading(false);
        setError(true);
        return;
      }

      container.innerHTML = "";

      try {
        const bytes = base64ToUint8Array(cleaned);
        pdfDoc = await pdfjs.getDocument({
          data: bytes,
          disableRange: true,
          disableStream: true,
          isEvalSupported: false,
        }).promise;
        if (cancelled) return;

        const cssWidth = await waitForLayoutWidth(
          container,
          window.innerWidth - 32,
        );
        if (cancelled) return;

        // WebView: DPR alto + páginas grandes → canvas estoura memória.
        const outputScale = isReactNativeWebView()
          ? 1
          : Math.min(window.devicePixelRatio || 1, 2);

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
          const page = await pdfDoc.getPage(pageNum);
          if (cancelled) return;

          const baseViewport = page.getViewport({ scale: 1 });
          const scale = cssWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { alpha: false });
          if (!context) {
            throw new Error("Canvas 2D indisponível");
          }

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";
          canvas.style.marginBottom = "12px";
          canvas.style.borderRadius = "4px";
          canvas.style.boxShadow = "0 1px 4px rgba(0,0,0,0.12)";
          canvas.style.background = "#fff";

          await page.render({
            canvasContext: context,
            viewport,
            transform:
              outputScale !== 1
                ? [outputScale, 0, 0, outputScale, 0, 0]
                : undefined,
          }).promise;

          container.appendChild(canvas);
        }

        if (!cancelled && container.childElementCount === 0) {
          throw new Error("Nenhuma página renderizada");
        }
      } catch (err) {
        console.error("MobilePdfViewer render error", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void renderPdf();

    return () => {
      cancelled = true;
      void pdfDoc?.destroy();
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [base64]);

  return (
    <Box sx={{ width: "100%", px: 1.5, pb: 2, boxSizing: "border-box" }}>
      {loading ? (
        <Stack alignItems="center" py={4}>
          <CircularLoading />
        </Stack>
      ) : null}

      {error ? (
        <Stack spacing={1.5} alignItems="center" py={3} px={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Não foi possível pré-visualizar o documento neste aparelho.
          </Typography>
          <Button variant="contained" onClick={handleDownloadFallback}>
            Baixar arquivo
          </Button>
        </Stack>
      ) : null}

      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          minHeight: loading || error ? 0 : 120,
          // Não usar display:none — precisa de largura para medir o canvas.
          visibility: error || loading ? "hidden" : "visible",
          height: error || loading ? 0 : "auto",
          overflow: error || loading ? "hidden" : "visible",
        }}
      />
    </Box>
  );
}
